import isNil from 'lodash/isNil'
import { useCallback, useState } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { tryReadFromLocalStorage } from '@omnea-app/fe-browser-utils/tryReadFromLocalStorage'
import { trySaveToLocalStorage } from '@omnea-app/fe-browser-utils/trySaveToLocalStorage'

const SNOOZE_DURATION_MS = 30 * 24 * 60 * 60 * 1000

const getSnoozedUntilKey = (organisationId: string, userId: string): string =>
  `omnea_org_config_issues_snoozed_until_${organisationId}_${userId}`

const readSnoozedUntil = (organisationId: string, userId: string): number | null => {
  const stored = tryReadFromLocalStorage({ key: getSnoozedUntilKey(organisationId, userId) })
  if (isNil(stored)) return null

  const snoozedUntil = Number(stored)
  return Number.isFinite(snoozedUntil) ? snoozedUntil : null
}

export const useOrgConfigIssuesSnooze = () => {
  const { user, org } = useAuthenticatedUser()
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(() => readSnoozedUntil(org.organisationId, user.id))

  const snooze = useCallback(() => {
    const nextSnoozedUntil = Date.now() + SNOOZE_DURATION_MS

    // The dismissal stands for the session even when the write fails, so the result is not acted on
    trySaveToLocalStorage({ key: getSnoozedUntilKey(org.organisationId, user.id), value: String(nextSnoozedUntil) })
    setSnoozedUntil(nextSnoozedUntil)
  }, [org.organisationId, user.id])

  return {
    isSnoozed: !isNil(snoozedUntil) && snoozedUntil > Date.now(),
    snooze,
  }
}
